import BottomMenu from "./components/BottomMenu";
import { useState, useEffect, useMemo } from "react";
import LoginView from "./views/LoginView";
import ConversationListView from "./views/ConversationListView";
import FundWalletView from "./views/FundWalletView";
import PendingHandshakesView from './views/PendingHandshakesView';
import PrivacyScoreView from "./views/PrivacyScoreView";
import NewChatView from "./views/NewChatView";
import NewContactView from "./views/NewContactView";
import NewGroupChatDetailsView from "./views/NewGroupChatDetailsView";
import NewGroupChatMembersView from "./views/NewGroupChatMembersView";
import SetPasswordView from "./views/SetPasswordView";
import SettingsView from "./views/SettingsView";
import SetupUnlinkSecretView from "./views/SetupUnlinkSecretView";
import ShowGeneratedSecretView from "./views/ShowGeneratedSecretView";
import RevealSecretView from "./views/RevealSecretView";
import ImportSecretView from "./views/ImportSecretView";
import SetWipePasswordView from "./views/SetWipePasswordView";
import DataWipedView from "./views/DataWipedView";
import ChatView from './views/ChatView';

import { 
  encryptData, 
  decryptData, 
  decryptSecret,
  deriveKeyFromPassword, 
  deriveUserId, 
  deriveViewKey,
  generateSecret,
  unlinkSecret,
} from "./utils/CryptoHelpers";
import {
  deleteContact,
} from "./db/ContactStorage";
import {
  handleLogin,
  handleLogout,
  initializeAccount,
} from "./db/AuthManager";
import { UnlinkProvider } from "./context/UnlinkContext";
import { usePasswordAndUserId } from "./context/AuthContext";
import { initDB } from './db';

// set db
await initDB().catch(console.error);

function App() {
  const [viewStack, setViewStack] = useState(["login"]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [typing, setTyping] = useState(false);
  const [contacts, setContacts] = useState([]); // start empty
  const [groupMembers, setGroupMembers] = useState([]);
  const [privacyScore, setPrivacyScore] = useState("");
  const [privacyDescription, setPrivacyDescription] = useState("");

  const { password, setPassword, userId, setUserId, salt, setSalt } = usePasswordAndUserId();

  const view = viewStack[viewStack.length - 1];

  const navigate = (newView) => {
    setViewStack((prev) => [...prev, newView]);
  };

  const goBack = () => {
    setViewStack((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));
  };

  const handleSetPassword = async (newPassword) => {
    try {
      await initializeAccount(newPassword);

      // Instead of setting state manually, just re-use login logic
      await handleLogin(
        newPassword,
        setPassword,
        setUserId,
        setSalt,
        setContacts,
        navigate
      );
    } catch (e) {
      alert(e.message);
    }
  };

  const handleSelectContact = (contact) => { console.log('handleSelectContact', contact);
    setSelectedContact(contact);
    setMessages([{ message: `Chatting with ${contact.name}`, sender: "system" }]);
    navigate("chat");
  };

  const handleBack = () => {
    navigate("contacts");
    setSelectedContact(null);
  };

  const memoizedContacts = useMemo(() => contacts, [contacts]);

  let content;
  if (view === "login") {
    content = (
      <LoginView
        password={password}
        setPassword={setPassword}
        onTryLogin={() =>
          handleLogin(
            password,
            setPassword,
            setUserId,
            setSalt,
            setContacts,
            navigate
          )
        }
        onNewAccount={() => {
          navigate("set_password");
        }}
      />
    );
  } else if (view === "set_password") {
    content = (
      <SetPasswordView
        onSetPassword={handleSetPassword}
        onBack={goBack}
      />
    );
  } else if (view === "contacts") {
    content = (
      <ConversationListView
        contacts={memoizedContacts}
        onFundWallet={() => navigate("fund_wallet")}
        navigate={navigate}
        setContacts={setContacts}
      />
    );
  } else if (view === "new_contact") {
    content = (
      <NewContactView
        contacts={contacts}
        onBack={goBack}
        onNewContact={() => navigate("new_chat")}
        setContacts={setContacts}
      />
    );
  } else if (view === "settings") {
    content = (
      <SettingsView
        onImportSecret={() => navigate("import_secret")}
        onRevealSecret={() => navigate("reveal_secret")}
        unlinkSecret={unlinkSecret}
        decryptSecret={decryptSecret}
        onLogout ={() => handleLogout({setPassword, setUserId, setContacts, navigate})}
        onFundWallet={() => navigate("fund_wallet")}
        onSetWipePassword={() => navigate("set_wipe_password")}
      />
    );
  } else if (view === "reveal_secret") {
      content = (
        <RevealSecretView
          password={password}
          unlinkSecret={unlinkSecret}
          decryptSecret={decryptSecret}
          onBack={goBack}
        />
      );
  } else if (view === "import_secret") {
    content = (
      <ImportSecretView
        onBack={goBack}
        navigate={() => navigate("contacts")}
      />
    );
  } else if (view === "new_chat") {
    content = (
      <NewChatView
        contacts={contacts}
        onNewContact={() => navigate("new_contact")}
        onNewGroup={() => navigate("new_group_chat_members")}
        navigate={navigate}
      />
    );
  } else if (view === "new_group_chat_details") {
    content = (
      <NewGroupChatDetailsView
        members={groupMembers}
        onBack={goBack}
        onGroupCreated={() => alert("Group chats coming soon.")}
      />
    );
  } else if (view === "new_group_chat_members") {
    content = (
      <NewGroupChatMembersView
        contacts={contacts}
        onBack={goBack}
        onNext={(selected) => {
          setGroupMembers(selected);
          navigate("new_group_chat_details");
        }}
      />
    );
  } else if (view === "setup_unlink_secret") {
    content = (
      <SetupUnlinkSecretView
        onImport={() => navigate("import_secret")}
        onBack={() => handleLogout(setPassword, setUserId, setContacts, setSelectedContact, navigate)}
        navigate={() => navigate("contacts")}
      />
    );
  } else if (view === "show_generated_secret") {
    content = (
      <ShowGeneratedSecretView
        //unlinkAddress={viewKey} // assuming this is the public view key
        unlinkAddress="2oB1kD7f8xJ3zW4yV9RnQsTmUvWxYz1aBcDeFgHiJkLmNoPqRs"
        mnemonic="wine pilot post viable unit vessel physical system click stone gown myself" //tmp
        onBack={goBack}
        onContinue={() => navigate("contacts")}
      />
    );
  } else if (view === "fund_wallet") {
    content = (
      <FundWalletView
        walletAddress={"5D9X3RbzK2m9Qeg1X8oywGZRjWqh9gWtiZHpU9xHnKUz"} // tmp
        onBack={goBack}
        onNavigatePrivacy={(score, desc) => {
          setPrivacyScore(score);
          setPrivacyDescription(desc);
          navigate("privacy_score");
        }}
      />
    );
  } else if (view === "privacy_score") {
    content = (
      <PrivacyScoreView
        score={privacyScore}
        description={privacyDescription}
        onBack={goBack}
      />
    );
  } else if (view === "set_wipe_password") {
    content = (
      <SetWipePasswordView
        onBack={() => navigate("settings")}
        currentPassword={password}
      />
    );
  } else if (view === "data_wiped") {
    content = <DataWipedView onBackToLogin={() => navigate("login")} />;
  } 
  // chat
  else if (view.name === "chat") {
    content = (
      <ChatView
        conversationId={view.props.conversationId}
        navigate={navigate}
        onBack={handleBack}
      />
    );
  }

  else if (view.name === "pendingHandshakesView") {
    return (
      <PendingHandshakesView
        onBack={() => navigate("contacts")}
      />
    );
  }

  const showMenu = ["contacts", "settings", "new_chat"].includes(view);
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        backgroundColor: "#000",
        color: "#fff",
      }}
    >
      <div style={{ flexGrow: 1, overflow: "hidden" }}>
        {content}
      </div>
      
      {showMenu && (
        <div style={{ flexShrink: 0 }}>
          <BottomMenu view={view} onNavigate={navigate} />
        </div>
      )}
    </div>
  );
}

export default App;